import { User, Film, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { ILayoutsPanelProps } from '@/@types/sceneEditorV2';
import type { SceneType } from '@/@types/reelScenes';

/** Split-screen icon: horizontal divider with image in top, person in bottom */
function SplitImageAvatarIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Frame */}
      <rect x="2" y="1" width="20" height="22" rx="2" />
      {/* Horizontal divider */}
      <line x1="2" y1="10" x2="22" y2="10" />
      {/* Image icon in top section (mountain + sun) */}
      <circle cx="18" cy="4.5" r="1.2" />
      <path d="M6 9.5l4-3.5 5 3.5" />
      {/* Person icon in bottom section */}
      <circle cx="12" cy="14.5" r="2" />
      <path d="M16.5 21c0-2.2-2-3.8-4.5-3.8s-4.5 1.6-4.5 3.8" />
    </svg>
  );
}

/** Split-screen icon: horizontal divider with person in bottom section */
function SplitAvatarIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {/* Frame */}
      <rect x="2" y="1" width="20" height="22" rx="2" />
      {/* Horizontal divider */}
      <line x1="2" y1="10" x2="22" y2="10" />
      {/* Person icon in bottom section */}
      <circle cx="12" cy="14.5" r="2" />
      <path d="M16.5 21c0-2.2-2-3.8-4.5-3.8s-4.5 1.6-4.5 3.8" />
    </svg>
  );
}

interface ILayoutOption {
  sceneType: SceneType;
  label: string;
  description: string;
  icon: LucideIcon | React.FC<{ className?: string }>;
}

const LAYOUT_OPTIONS: readonly ILayoutOption[] = [
  {
    sceneType: 'avatar_only',
    label: 'Avatar Only',
    description: 'Full-screen talking head avatar',
    icon: User,
  },
  {
    sceneType: 'video_only',
    label: 'Video Only',
    description: 'Full-screen b-roll video with voiceover',
    icon: Film,
  },
  {
    sceneType: 'avatar_broll_split',
    label: 'Avatar + Video',
    description: 'Split: b-roll/text top, avatar bottom',
    icon: SplitAvatarIcon,
  },
  {
    sceneType: 'avatar_image_split',
    label: 'Avatar + Image',
    description: 'Split: static image top, avatar bottom',
    icon: SplitImageAvatarIcon,
  },
] as const;

interface ILayoutCardProps {
  option: ILayoutOption;
  isSelected: boolean;
  onSelect: () => void;
}

function LayoutCard({ option, isSelected, onSelect }: ILayoutCardProps) {
  const Icon = option.icon;

  return (
    <button
      onClick={onSelect}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-lg p-3',
        'border bg-background-secondary',
        'transition-all cursor-pointer',
        isSelected
          ? 'border-primary ring-2 ring-primary/30 bg-primary/10 light:border-primary-foreground/40 light:ring-primary-foreground/20 light:bg-primary-foreground/8'
          : 'border-border hover:border-border-hover hover:bg-background-secondary/80'
      )}
    >
      <div className="flex h-10 items-center justify-center">
        <Icon
          className={cn(
            'size-7',
            isSelected ? 'text-primary light:text-primary-foreground' : 'text-foreground-muted'
          )}
        />
      </div>
      <div className="text-center">
        <span
          className={cn(
            'block text-sm font-medium leading-tight',
            isSelected ? 'text-primary light:text-primary-foreground' : 'text-foreground'
          )}
        >
          {option.label}
        </span>
        <span className="mt-0.5 block text-xs leading-tight text-foreground-muted">
          {option.description}
        </span>
      </div>
    </button>
  );
}

function LayoutsPanel({ onClose, selectedSceneType, onLayoutChange }: ILayoutsPanelProps) {
  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-base font-medium">Layouts</span>
        <button
          onClick={onClose}
          className="rounded-md p-0.5 text-foreground-muted transition-colors hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>

      {/* Layout cards */}
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-2 p-3">
          {LAYOUT_OPTIONS.map(option => (
            <LayoutCard
              key={option.sceneType}
              option={option}
              isSelected={selectedSceneType === option.sceneType}
              onSelect={() => onLayoutChange(option.sceneType)}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

export { LayoutsPanel };
